import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import adminClient, { setAuthToken } from '@/lib/api/adminClient';

interface AdminUser {
  email: string;
  token: string;
}

interface AdminAuthContext {
  user: AdminUser | null;
  isLoading: boolean;
  isAdmin: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  loginWithToken: (token: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AdminAuthContext | null>(null);

export function useAdminAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAdminAuth must be inside AdminAuthProvider');
  return ctx;
}

export function useAdminAuthProvider(): AdminAuthContext {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const verifyAdmin = useCallback(async (token: string): Promise<boolean> => {
    setAuthToken(token);
    try {
      await adminClient.get('/admin/stats');
      return true;
    } catch (err: any) {
      if (err.response?.status === 403) return false;
      throw err;
    }
  }, []);

  // Check stored token on mount
  useEffect(() => {
    const stored = localStorage.getItem('admin_token');
    if (stored) {
      verifyAdmin(stored)
        .then((ok) => {
          if (ok) {
            const email = localStorage.getItem('admin_email') || '';
            setUser({ email, token: stored });
            setIsAdmin(true);
          } else {
            localStorage.removeItem('admin_token');
            localStorage.removeItem('admin_email');
          }
        })
        .catch(() => {
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_email');
        })
        .finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, [verifyAdmin]);

  const login = useCallback(async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const { data } = await adminClient.post('/auth/login', { email, password });
      const token = data.access_token || data.token;
      const ok = await verifyAdmin(token);
      if (!ok) {
        setError('This account does not have admin privileges.');
        setAuthToken(null);
        setIsLoading(false);
        return;
      }
      localStorage.setItem('admin_token', token);
      localStorage.setItem('admin_email', email);
      if (data.refresh_token) {
        localStorage.setItem('admin_refresh_token', data.refresh_token);
      }
      setUser({ email, token });
      setIsAdmin(true);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  }, [verifyAdmin]);

  const loginWithToken = useCallback(async (token: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const ok = await verifyAdmin(token);
      if (!ok) {
        setError('This account does not have admin privileges.');
        setAuthToken(null);
        setIsLoading(false);
        return;
      }
      localStorage.setItem('admin_token', token);
      setUser({ email: 'admin', token });
      setIsAdmin(true);
    } catch (err: any) {
      setError('Token verification failed');
    } finally {
      setIsLoading(false);
    }
  }, [verifyAdmin]);

  const logout = useCallback(() => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_email');
    localStorage.removeItem('admin_refresh_token');
    setAuthToken(null);
    setUser(null);
    setIsAdmin(false);
  }, []);

  return { user, isLoading, isAdmin, error, login, loginWithToken, logout };
}

export { AuthContext };
